import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addClassProgramBeat2Packs,
  getClassProgramLive, getClassProgramPulseSummary, getClassProgramTakeHomes, resetClassProgramSeatPictures,
  rotateClassProgramTakeHome, setClassProgramFocus, setClassProgramSeatFocus,
  setClassProgramFreeBeats, setClassProgramRecording, setClassProgramSeatFree,
  setClassProgramSeatUnlocked, setClassProgramTakehomeOpen, unlockClassProgramSeat,
} from '@/api/classPrograms';
import { fetchPagedClassLoops } from '@/components/Admin/ClassPackBuilder';
import { generateSeatPacks } from '@/state/seatPacks';
import { BattlesPanel } from '@/components/Battles/BattlesPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ltbb-ba-token';

const GROUPS = ['melody', 'drums', 'texture', 'fx'];
const COLORS = {
  melody: 'var(--ba-class-melodies)', drums: 'var(--ba-class-drums)',
  texture: 'var(--ba-class-textures)', fx: 'var(--ba-class-sound-effects)',
};

function seenStatus(seat, now = Date.now()) {
  if (seat.locked) return 'Locked';
  if (!seat.claimed) return 'Not joined';
  if (!seat.last_seen_at) return 'Joined';
  const seen = new Date(seat.last_seen_at).getTime();
  if (now - seen <= 120000) return 'Here now';
  return `Last seen ${new Date(seat.last_seen_at).toLocaleString()}`;
}

function BeatSummary({ slot, beat, adminTakehome, onRotate }) {
  const summary = beat?.summary || null;
  const takehome = beat?.takehome || { status: 'not_made', opens: 0, downloads: 0, has_feedback: false };
  return <div className="live-beat-summary" data-testid={`beat-${slot}-summary`}>
    <strong>Beat {slot}</strong>
    {!summary ? <span>Not started</span> : <>
      <span>{summary.phase === 'arrange' ? 'Arranging' : 'Picking'}</span>
      <div className="live-pick-counts">{GROUPS.map((group) => <span key={group} style={{ '--summary-color': COLORS[group] }} title={`${group}: ${summary.picked_counts?.[group] || 0}`}><i />{summary.picked_counts?.[group] || 0}</span>)}</div>
      <span>Step {Math.min(6, Number(summary.furthest_step || 0) + 1)} of 6</span>
      <span>{Number(summary.song_end_bars || 0)} bars</span>
    </>}
    <div className="live-takehome">
      <b>Take-home</b>
      <span>{takehome.status === 'ready' ? `Ready (made ${new Date(takehome.created_at).toLocaleString()})` : 'Not made'}</span>
      <span>Link opens {Number(takehome.opens || 0)}</span>
      <span>Downloads {Number(takehome.downloads || 0)}</span>
      <span className={takehome.has_feedback ? 'has-feedback' : ''}><i aria-hidden="true" />{takehome.has_feedback ? 'Feedback answered' : 'No feedback'}</span>
      {adminTakehome && <button className="live-rotate-link" onClick={() => onRotate(adminTakehome)}>Make a new link</button>}
    </div>
  </div>;
}

function ProgramResults({ summary }) {
  if (!summary) return <section className="program-results" aria-label="Program results"><span>Loading Program results...</span></section>;
  const feedback = summary.feedback || {};
  return <section className="program-results" aria-label="Program results">
    <div className="program-results-heading"><h2>Program results</h2>{summary.wiped && <span>Saved after wipe</span>}</div>
    <div className="program-results-counts">
      <span><b>{summary.seats_joined}</b>Joined / {summary.seats_total} seats</span>
      <span><b>{summary.beats_started}</b>Beats started</span>
      <span><b>{summary.beats_arranged}</b>Beats arranged</span>
      <span><b>{summary.beats_finished}</b>Beats finished</span>
      <span><b>{summary.takehomes_created}</b>Take-homes</span>
      <span><b>{summary.free_beats_made || 0}</b>Free beats made</span>
      <span><b>{summary.kids_with_free_beats || 0}</b>Kids with free beats</span>
      <span><b>{summary.link_opens}</b>Link opens</span>
      <span><b>{summary.link_downloads}</b>Downloads</span>
      <span><b>{feedback.beats_with_feedback || 0}</b>Beats with feedback</span>
    </div>
    <div className="program-results-feedback">
      <span>Feel: Meh {feedback.feel?.meh || 0} · Good {feedback.feel?.good || 0} · Fire {feedback.feel?.fire || 0}</span>
      <span>Learned: No {feedback.learned?.no || 0} · A little {feedback.learned?.a_little || 0} · A lot {feedback.learned?.a_lot || 0}</span>
      <span>More music: No {feedback.more?.no || 0} · Maybe {feedback.more?.maybe || 0} · Yes {feedback.more?.yes || 0}</span>
    </div>
    {(summary.notes || []).map((note) => <p key={note}>{note}</p>)}
  </section>;
}

// Only offer Add Beat 2 packs while some kid has neither Beat 2 sounds nor a started Beat 2.
export const needsBeat2Packs = (seats = []) => seats.some((seat) => !seat.has_pack2
  && !(seat.beats || []).some((beat) => beat.slot === 2));

export function ClassProgramLiveView() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [pending, setPending] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminTakehomes, setAdminTakehomes] = useState([]);
  const [beat2Summary, setBeat2Summary] = useState('');
  const [beat2Complete, setBeat2Complete] = useState(false);
  useEffect(() => {
    let active = true;
    fetch(`${BACKEND_URL}/api/v1/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}` } })
      .then((response) => response.ok ? response.json() : null)
      .then((me) => { if (active) setIsAdmin(['admin', 'ltbb_admin'].includes(me?.role)); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const load = useCallback(async () => {
    try {
      const [live, pulse, takehomes] = await Promise.all([
        getClassProgramLive(programId), getClassProgramPulseSummary(programId),
        isAdmin ? getClassProgramTakeHomes(programId) : Promise.resolve([]),
      ]);
      setData(live); setSummary(pulse); setAdminTakehomes(takehomes); setError('');
    }
    catch (requestError) { setError(requestError.message); }
  }, [isAdmin, programId]);
  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  const mutate = async (key, action) => {
    setPending(key); setError('');
    try { await action(); await load(); }
    catch (requestError) { setError(requestError.message); }
    finally { setPending(''); }
  };
  const addBeat2Packs = async () => {
    setPending('beat2-packs'); setError(''); setBeat2Summary('');
    try {
      const entries = await Promise.all(GROUPS.map(async (group) => [group, await fetchPagedClassLoops(group)]));
      const catalog = Object.fromEntries(entries);
      // Each Beat 2 pack avoids that kid's Beat 1 sounds (the server rejects packs that overlap over 50%).
      const generated = generateSeatPacks({ catalog, seatCount: data.seats.length,
        excludedIdsBySeat: data.seats.map((seat) => new Set(seat.pack_loop_ids || [])) });
      const response = await addClassProgramBeat2Packs(programId, data.seats.map((seat, index) => ({
        seat_number: seat.seat_number,
        pack: { ...generated.packs[index], id: `program-seat-${seat.seat_number}-beat-2`, name: `${data.program.name} · Seat ${seat.seat_number} · Beat 2` },
      })));
      const counts = response.results.reduce((result, item) => ({ ...result, [item.status]: (result[item.status] || 0) + 1 }), {});
      const parts = [];
      if (counts.added) parts.push(`Added new Beat 2 sounds for ${counts.added} ${counts.added === 1 ? 'kid' : 'kids'}.`);
      if (counts.skipped_has_beat2) parts.push(`${counts.skipped_has_beat2} ${counts.skipped_has_beat2 === 1 ? 'kid already started' : 'kids already started'} Beat 2 and keep their sounds.`);
      if (counts.skipped_has_pack2) parts.push(`${counts.skipped_has_pack2} ${counts.skipped_has_pack2 === 1 ? 'kid already has' : 'kids already have'} Beat 2 sounds.`);
      if (counts.invalid || counts.unknown_seat) parts.push(`${(counts.invalid || 0) + (counts.unknown_seat || 0)} could not be added. Try again.`);
      setBeat2Summary(parts.join(' '));
      setBeat2Complete(!counts.added && !counts.invalid && !counts.unknown_seat);
      await load();
    } catch (requestError) { setError(requestError.message); }
    finally { setPending(''); }
  };
  if (!data) return <main className="class-live-view class-live-loading" data-skin="playground"><p>{error || 'Loading class...'}</p><button onClick={() => navigate('/facilitator')}>Back</button></main>;
  return <main className="class-live-view" data-skin="playground">
    <header className="class-live-header">
      <button onClick={() => navigate('/facilitator')}>Back</button>
      <div><h1>{data.program.name}</h1><strong>{data.program.class_code}</strong><span>{data.program.status}</span></div>
      <label className="class-live-focus"><input type="checkbox" checked={data.program.focus_default} onChange={(event) => mutate('class-focus', () => setClassProgramFocus(programId, event.target.checked))} disabled={pending === 'class-focus'} />Focus for the whole class</label>
      <label className="class-live-focus"><input type="checkbox" checked={data.program.takehome_open} onChange={(event) => mutate('showcase-time', () => setClassProgramTakehomeOpen(programId, event.target.checked))} disabled={pending === 'showcase-time'} />Showcase time</label>
      <label className="class-live-focus"><input type="checkbox" checked={Boolean(data.program.free_unlocked_all)} onChange={(event) => mutate('class-free', () => setClassProgramFreeBeats(programId, event.target.checked))} disabled={pending === 'class-free'} />Unlock free beats for everyone</label>
      <label className="class-live-focus"><input type="checkbox" checked={Boolean(data.program.recording_enabled)} onChange={(event) => mutate('recording', () => setClassProgramRecording(programId, event.target.checked))} disabled={pending === 'recording'} />Mic recording in free beats</label>
      <button onClick={() => navigate(`/facilitator/class/${programId}/showcase`)}>Showcase playlist</button>
      <button onClick={() => navigate(`/facilitator/class/${programId}/projector`)}>Projector mode</button>
      <button onClick={load}>Refresh</button>
    </header>
    {data.program.status === 'active' && !beat2Complete && needsBeat2Packs(data.seats) && <div className="class-live-beat2"><button disabled={pending === 'beat2-packs'} onClick={addBeat2Packs}>{pending === 'beat2-packs' ? 'Adding Beat 2 packs...' : 'Add Beat 2 packs'}</button>{beat2Summary && <p role="status">{beat2Summary}</p>}</div>}
    {error && <p className="class-live-error" role="alert">{error}</p>}
    <ProgramResults summary={summary} />
    <BattlesPanel program={data.program} seats={data.seats} />
    <section className="class-live-grid">
      {data.seats.map((seat) => {
        const beats = Object.fromEntries((seat.beats || []).map((beat) => [beat.slot, beat]));
        const takehomes = Object.fromEntries(adminTakehomes.filter((item) => item.seat_number === seat.seat_number).map((item) => [item.slot, item]));
        return <article className={`class-live-seat${seenStatus(seat) === 'Here now' ? ' is-here' : ''}`} key={seat.seat_number} data-testid={`live-seat-${seat.seat_number}`}>
          <div className="class-live-seat-heading"><div><span>Seat</span><strong>{seat.seat_number}</strong></div><div>{seat.display_name && <h2>{seat.display_name}</h2>}<p>{seenStatus(seat)}</p></div></div>
          {seat.save_failing_since && <div className="class-live-save-warning" role="status">Saves failing</div>}
          <div className="class-live-free-summary"><strong>{seat.free_beat_count || 0}</strong><span>{(seat.free_beat_count || 0) === 1 ? 'free beat' : 'free beats'}</span>{seat.showcase_warning && <b>{seat.showcase_warning}</b>}</div>
          <div className="class-live-beats"><BeatSummary slot={1} beat={beats[1]} adminTakehome={takehomes[1]} onRotate={(item) => {
            if (window.confirm(`Make a new take-home link for seat ${seat.seat_number}, Beat 1? The old link will stop working.`)) mutate(`rotate-${item.takehome_id}`, () => rotateClassProgramTakeHome(programId, item.takehome_id));
          }} /><BeatSummary slot={2} beat={beats[2]} adminTakehome={takehomes[2]} onRotate={(item) => {
            if (window.confirm(`Make a new take-home link for seat ${seat.seat_number}, Beat 2? The old link will stop working.`)) mutate(`rotate-${item.takehome_id}`, () => rotateClassProgramTakeHome(programId, item.takehome_id));
          }} /></div>
          <div className="class-live-controls">
            <label>Focus<select aria-label={`Seat ${seat.seat_number} focus`} value={seat.focus_override == null ? 'default' : String(seat.focus_override)} onChange={(event) => {
              const value = event.target.value === 'default' ? null : event.target.value === 'true';
              mutate(`focus-${seat.seat_number}`, () => setClassProgramSeatFocus(programId, seat.seat_number, value));
            }}><option value="default">Class default</option><option value="true">On</option><option value="false">Off</option></select></label>
            <label><input type="checkbox" checked={seat.unlocked_all} onChange={(event) => mutate(`all-${seat.seat_number}`, () => setClassProgramSeatUnlocked(programId, seat.seat_number, event.target.checked))} />Unlock everything</label>
            <label><input type="checkbox" checked={Boolean(seat.free_unlocked)} onChange={(event) => mutate(`free-${seat.seat_number}`, () => setClassProgramSeatFree(programId, seat.seat_number, event.target.checked))} />Free beats grant</label>
            <button onClick={() => {
              if (window.confirm(`Reset pictures for seat ${seat.seat_number}? Stay with the student: they have 10 minutes to pick new pictures.`)) mutate(`reset-${seat.seat_number}`, () => resetClassProgramSeatPictures(programId, seat.seat_number));
            }}>Reset pictures</button>
            {seat.locked && <button onClick={() => mutate(`unlock-${seat.seat_number}`, () => unlockClassProgramSeat(programId, seat.seat_number))}>Unlock seat</button>}
          </div>
        </article>;
      })}
    </section>
  </main>;
}

export default ClassProgramLiveView;
